/** Series (D-27): lo que se hace con los ficheros de un release cuando son episodios.
 *
 *  Vive en el paquete compartido porque lo usan los dos lados: la ficha del navegador pinta
 *  cada versión de una serie agrupada por temporada, y la API encola «la temporada N de este
 *  grupo» a partir de la misma agrupación. Dos agrupaciones distintas acabarían encolando
 *  algo distinto de lo que se enseñó. */

export interface FicheroEpisodio {
  kind: string;
  season: number | null;
  episode: number | null;
  sizeBytes: number | null;
}

export interface Temporada<F extends FicheroEpisodio> {
  /** `null` es «sin numerar»: el sitio no dijo la temporada y no se inventa. */
  season: number | null;
  ficheros: F[];
  /** Cuántos vídeos, que es lo que cuenta como episodios. */
  episodios: number;
  /** Suma de lo que se sabe. `null` si no se sabe de ninguno. */
  sizeBytes: number | null;
}

/** Los vídeos de un grupo repartidos por temporada, en orden, y los sin temporada al final.
 *
 *  Solo los vídeos: un `.srt` suelto en el grupo no es un episodio y encolarlo como si lo
 *  fuera metería en aMule algo que la conversión no sabe qué hacer con ello. Dentro de una
 *  temporada se ordena por episodio y, a igual episodio o sin él, por posición en el grupo,
 *  que es el orden en que el sitio los publicó. */
export function agruparPorTemporada<F extends FicheroEpisodio & { position: number }>(
  ficheros: F[],
): Temporada<F>[] {
  const porTemporada = new Map<number | null, F[]>();
  for (const f of ficheros) {
    if (f.kind !== 'video') continue;
    const lista = porTemporada.get(f.season);
    if (lista) lista.push(f);
    else porTemporada.set(f.season, [f]);
  }

  const temporadas = [...porTemporada.entries()].sort(([a], [b]) => {
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  });

  return temporadas.map(([season, lista]) => {
    const ordenados = [...lista].sort((a, b) => {
      if (a.episode != null && b.episode != null && a.episode !== b.episode) return a.episode - b.episode;
      if (a.episode == null && b.episode != null) return 1;
      if (a.episode != null && b.episode == null) return -1;
      return a.position - b.position;
    });
    const conTamano = ordenados.filter((f) => f.sizeBytes != null);
    return {
      season,
      ficheros: ordenados,
      episodios: ordenados.length,
      sizeBytes: conTamano.length ? conTamano.reduce((acc, f) => acc + (f.sizeBytes ?? 0), 0) : null,
    };
  });
}

/** `1x03`, o `E3` si no hay temporada, o `null` si no hay ni episodio. Es la forma en que
 *  HispaShare lo escribe más veces, y la que se lee de un vistazo en una lista larga. */
export function etiquetaEpisodio(
  season: number | null,
  episode: number | null,
  episodeEnd?: number | null,
): string | null {
  if (episode == null) return season == null ? null : `T${season}`;
  const ep = String(episode).padStart(2, '0');
  // Dos seguidos en el mismo fichero: `1x01-02`.
  const rango = episodeEnd != null && episodeEnd > episode ? `-${String(episodeEnd).padStart(2, '0')}` : '';
  return season == null ? `E${ep}${rango}` : `${season}x${ep}${rango}`;
}

/** «Temporada 3», o «Sin numerar» para los ficheros a los que el sitio no puso temporada. */
export function nombreTemporada(season: number | null): string {
  return season == null ? 'Sin numerar' : `Temporada ${season}`;
}

/** Las claves `temporada:episodio` que cubre un fichero: una, o varias si trae episodios
 *  seguidos. Es la moneda con la que se cruza contra lo que ya hay en Plex: un episodio está
 *  «en Plex» si TODAS sus claves lo están. Vacío si al fichero le falta la temporada o el
 *  número, porque entonces no hay nada que cruzar. */
export function clavesDeEpisodio(
  season: number | null,
  episode: number | null,
  episodeEnd?: number | null,
): string[] {
  if (season == null || episode == null) return [];
  const hasta = episodeEnd != null && episodeEnd > episode ? episodeEnd : episode;
  const claves: string[] = [];
  for (let e = episode; e <= hasta; e++) claves.push(`${season}:${e}`);
  return claves;
}
